import { type FindingProgressContent } from '../../utils/api-client/sse-schemas.ts'
import { type ActivityStepStatus } from './activity-step-card.tsx'

type Step = { title: string; description: string; status: ActivityStepStatus }

/**
 * Upsert an in-progress step keyed by title. A title that already has a step
 * updates it in place (reopening it to in_progress) rather than appending a
 * duplicate card, so activities that re-emit progress — e.g. after an SSE
 * reconnect replays buffered events — collapse onto their existing step.
 */
function applyInProgressEvent({
  steps,
  stepsByTitle,
  event,
}: {
  steps: Step[]
  stepsByTitle: Map<string, Step>
  event: FindingProgressContent
}): void {
  const existingStep = stepsByTitle.get(event.title)
  if (existingStep) {
    existingStep.description = event.description
    existingStep.status = 'in_progress'
    return
  }
  const step: Step = {
    title: event.title,
    description: event.description,
    status: 'in_progress',
  }
  steps.push(step)
  stepsByTitle.set(event.title, step)
}

/**
 * Mark prior in-progress steps as completed when a `completed` event arrives.
 * When the completed event's title matches an in-progress step's title, the
 * step adopts the completed event's description — letting emitters surface
 * richer result text (e.g. "Retrieved 3 feedback entries") after a generic
 * in-progress prompt (e.g. "Searching prior feedback").
 */
function applyCompletedEvent(steps: Step[], completedEvent: FindingProgressContent): void {
  for (const step of steps) {
    if (step.status !== 'in_progress') {
      continue
    }
    step.status = 'completed'
    if (step.title === completedEvent.title) {
      step.description = completedEvent.description
    }
  }
}

/**
 * Transform SSE events into activity step cards.
 * An in_progress event upserts a step keyed by title (see
 * `applyInProgressEvent`) so activities that re-emit progress collapse onto
 * their existing card instead of duplicating. A completed event marks all prior
 * in-progress steps as completed, and replaces the description of the matching
 * step (see `applyCompletedEvent`). Handles both strict pairs and consecutive
 * in_progress events from production.
 */
export function eventsToSteps(
  events: FindingProgressContent[],
  hasCompletedEvent: boolean,
  showConfirmation: boolean
): Step[] {
  const steps: Step[] = []
  const stepsByTitle = new Map<string, Step>()

  for (const event of events) {
    if (event.status === 'in_progress') {
      applyInProgressEvent({ steps, stepsByTitle, event })
    } else if (event.status === 'completed') {
      applyCompletedEvent(steps, event)
    }
  }

  if (hasCompletedEvent || showConfirmation) {
    for (const step of steps) {
      step.status = 'completed'
    }
  }

  return steps
}
